import { useEffect, useId, useState } from "react";
import type { SoundsManifest } from "../../app/sounds";
import { useLive, useProject, useServices } from "../context";

/** The built-in sound list (null while loading or when it failed). */
export function useSoundList(): SoundsManifest | null {
  const { controller } = useServices();
  const [m, setM] = useState<SoundsManifest | null>(null);
  useEffect(() => {
    let alive = true;
    controller.sounds.manifest().then((x) => { if (alive) setM(x); }, () => { /* picker shows synth only */ });
    return () => { alive = false; };
  }, [controller]);
  return m;
}

const SYNTH = "synth", KIT = "drumkit";

/**
 * Instrument choice for a track: the built-in synth (or synthesised kit for
 * drums) or one of the recorded instruments (F-LIB-05).
 */
export function SoundPicker({ trackId, drums = false }: { trackId: string; drums?: boolean }) {
  const { controller } = useServices();
  const id = useId();
  const inst = useProject((p) => p.tracks.find((t) => t.id === trackId)?.instrument);
  const status = useLive((s) => s.sounds[trackId]);
  const list = useSoundList();
  if (!inst) return null;
  const value = inst.kind === "multisample" ? inst.soundId : inst.kind;
  const sounds = (list?.instruments ?? []).filter((s) => (drums ? s.type === "drums" : s.type === "pitched"));
  const groups = [...new Set(sounds.map((s) => s.category))];
  const choose = (v: string) => {
    if (v === SYNTH) void controller.setTrackInstrument(trackId, { kind: "synth" });
    else if (v === KIT) void controller.setTrackInstrument(trackId, { kind: "drumkit" });
    else void controller.setTrackInstrument(trackId, { kind: "multisample", soundId: v });
  };
  const loading = inst.kind === "multisample" && status?.soundId === inst.soundId && status.status === "loading";
  return (
    <div className="sound-picker">
      <label htmlFor={id}>Sound</label>
      <select id={id} value={value} onChange={(e) => choose(e.currentTarget.value)}>
        {drums ? <option value={KIT}>Synthesised kit</option> : <option value={SYNTH}>Synthesizer</option>}
        {groups.map((g) => (
          <optgroup key={g} label={`Recorded · ${g}`}>
            {sounds.filter((s) => s.category === g).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </optgroup>
        ))}
        {/* Keep an unknown id (file from a newer version) selectable so the value stays visible. */}
        {inst.kind === "multisample" && list && !sounds.some((s) => s.id === inst.soundId) && <option value={inst.soundId}>{inst.soundId} (not available)</option>}
      </select>
      {loading && <span className="status" role="status">Loading sound…</span>}
    </div>
  );
}
